import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreVertical, 
  Trash2,
  Users,
  DollarSign,
  TrendingUp,
  Save,
  X,
  Upload,
  FileSpreadsheet
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STAGES = [
  { key: 'qualified', label: 'Qualified' },
  { key: 'discover', label: 'Discover' },
  { key: 'market', label: 'Market' },
  { key: 'propose', label: 'Propose' }
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const ProducerPipeline = () => {
  const { isAdmin, isELT } = useAuth();
  const [loading, setLoading] = useState(true);
  const [producers, setProducers] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [producerToDelete, setProducerToDelete] = useState(null);
  const [newProducerName, setNewProducerName] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const canEdit = isAdmin() || isELT();

  const fetchProducers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pipeline/producers`);
      setProducers(response.data);
    } catch (error) {
      console.error('Failed to fetch producers:', error);
      toast.error('Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducers();
  }, [fetchProducers]);

  // Calculate totals row
  const totals = STAGES.reduce((acc, stage) => {
    acc[stage.key] = {
      account_count: producers.reduce((sum, p) => sum + (p[stage.key]?.account_count || 0), 0),
      estimated_premium: producers.reduce((sum, p) => sum + (p[stage.key]?.estimated_premium || 0), 0)
    };
    return acc;
  }, {});
  
  const grandTotalAccounts = producers.reduce((sum, p) => sum + p.total_accounts, 0);
  const grandTotalPremium = producers.reduce((sum, p) => sum + p.total_premium, 0);

  // Handle cell click for editing
  const handleCellClick = (producerId, stage, field, currentValue) => {
    if (!canEdit) return;
    setEditingCell({ producerId, stage, field });
    setEditValue(currentValue?.toString() || '0');
  };

  // Handle save cell
  const handleSaveCell = async () => {
    if (!editingCell) return;
    
    const { producerId, stage, field } = editingCell;
    const producer = producers.find(p => p.id === producerId);
    if (!producer) return;

    const numValue = field === 'account_count' 
      ? parseInt(editValue) || 0 
      : parseFloat(editValue) || 0;

    const updatedStageData = {
      ...producer[stage],
      [field]: numValue
    };

    try {
      await axios.put(`${API}/pipeline/producers/${producerId}`, {
        [stage]: updatedStageData
      });
      
      // Update local state
      setProducers(prev => prev.map(p => {
        if (p.id === producerId) {
          const updated = { ...p, [stage]: updatedStageData };
          // Recalculate totals
          let totalAccounts = 0;
          let totalPremium = 0;
          STAGES.forEach(s => {
            totalAccounts += updated[s.key]?.account_count || 0;
            totalPremium += updated[s.key]?.estimated_premium || 0;
          });
          return { ...updated, total_accounts: totalAccounts, total_premium: totalPremium };
        }
        return p;
      }));
      
      toast.success('Updated');
    } catch (error) {
      toast.error('Failed to update');
    }
    
    setEditingCell(null);
    setEditValue('');
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Handle key press in edit input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSaveCell();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Add new producer
  const handleAddProducer = async () => {
    if (!newProducerName.trim()) {
      toast.error('Please enter a producer name');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/pipeline/producers`, {
        producer_name: newProducerName.trim()
      });
      toast.success('Producer added');
      setAddDialogOpen(false);
      setNewProducerName('');
      fetchProducers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add producer');
    } finally {
      setSaving(false);
    }
  };

  // Delete producer
  const handleDeleteProducer = async () => {
    if (!producerToDelete) return;

    setSaving(true);
    try {
      await axios.delete(`${API}/pipeline/producers/${producerToDelete.id}`);
      toast.success('Producer deleted');
      setDeleteDialogOpen(false);
      setProducerToDelete(null);
      fetchProducers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete producer');
    } finally {
      setSaving(false);
    }
  };

  // Upload Excel file
  const handleUploadFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/pipeline/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Uploaded successfully! Created: ${response.data.producers_created}, Updated: ${response.data.producers_updated}`);
      setUploadDialogOpen(false);
      fetchProducers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      // Reset the input
      event.target.value = '';
    }
  };

  // Render editable cell
  const renderEditableCell = (producer, stage, field) => {
    const value = producer[stage.key]?.[field] || 0;
    const isEditing = editingCell?.producerId === producer.id && 
                      editingCell?.stage === stage.key && 
                      editingCell?.field === field;
    
    if (isEditing) {
      return (
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyPress}
            className="w-24 h-8 text-sm"
            autoFocus
          />
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleSaveCell}>
            <Save className="w-3 h-3 text-green-600" />
          </Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleCancelEdit}>
            <X className="w-3 h-3 text-red-600" />
          </Button>
        </div>
      );
    }

    const displayValue = field === 'estimated_premium' 
      ? formatCurrency(value)
      : value.toLocaleString();

    return (
      <span 
        className={`${canEdit ? 'cursor-pointer hover:bg-slate-100 px-2 py-1 rounded' : ''}`}
        onClick={() => handleCellClick(producer.id, stage.key, field, value)}
      >
        {displayValue}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <Header title="Producer Pipeline" />
        <div className="p-4 sm:p-6 space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Producer Pipeline" />
      
      <div className="p-4 sm:p-6 space-y-6" data-testid="producer-pipeline">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{producers.length}</p>
                  <p className="text-xs text-slate-500">Producers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{grandTotalAccounts.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Total Accounts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="md:col-span-2">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(grandTotalPremium)}</p>
                  <p className="text-xs text-slate-500">Total Pipeline Premium</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center">
          <p className="text-sm text-slate-500">
            {canEdit ? 'Click on any cell to edit values' : 'View-only access'}
          </p>
          {canEdit && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setUploadDialogOpen(true)} data-testid="upload-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload Excel
              </Button>
              <Button onClick={() => setAddDialogOpen(true)} data-testid="add-producer-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Producer
              </Button>
            </div>
          )}
        </div>

        {/* Pipeline Table */}
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-slate-900 w-48">Producer</TableHead>
                  {STAGES.map(stage => (
                    <TableHead key={stage.key} className="text-center" colSpan={2}>
                      <div className="font-semibold text-slate-900">{stage.label}</div>
                      <div className="flex text-xs text-slate-500 font-normal mt-1">
                        <span className="flex-1">Accounts</span>
                        <span className="flex-1">Premium</span>
                      </div>
                    </TableHead>
                  ))}
                  <TableHead className="text-center" colSpan={2}>
                    <div className="font-semibold text-slate-900">Totals</div>
                    <div className="flex text-xs text-slate-500 font-normal mt-1">
                      <span className="flex-1">Accounts</span>
                      <span className="flex-1">Premium</span>
                    </div>
                  </TableHead>
                  {canEdit && isAdmin() && (
                    <TableHead className="w-12"></TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {producers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8 text-slate-500">
                      No producers added yet. Click "Add Producer" to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {producers.map(producer => (
                      <TableRow key={producer.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium text-slate-900">
                          {producer.producer_name}
                        </TableCell>
                        {STAGES.map(stage => (
                          <>
                            <TableCell key={`${stage.key}-accounts`} className="text-center">
                              {renderEditableCell(producer, stage, 'account_count')}
                            </TableCell>
                            <TableCell key={`${stage.key}-premium`} className="text-center">
                              {renderEditableCell(producer, stage, 'estimated_premium')}
                            </TableCell>
                          </>
                        ))}
                        <TableCell className="text-center font-semibold bg-slate-50">
                          {producer.total_accounts.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-center font-semibold bg-slate-50">
                          {formatCurrency(producer.total_premium)}
                        </TableCell>
                        {canEdit && isAdmin() && (
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => {
                                    setProducerToDelete(producer);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    
                    {/* Totals Row */}
                    <TableRow className="bg-slate-100 font-semibold border-t-2">
                      <TableCell className="text-slate-900">TOTAL</TableCell>
                      {STAGES.map(stage => (
                        <>
                          <TableCell key={`total-${stage.key}-accounts`} className="text-center">
                            {totals[stage.key].account_count.toLocaleString()}
                          </TableCell>
                          <TableCell key={`total-${stage.key}-premium`} className="text-center">
                            {formatCurrency(totals[stage.key].estimated_premium)}
                          </TableCell>
                        </>
                      ))}
                      <TableCell className="text-center bg-blue-50 text-blue-900">
                        {grandTotalAccounts.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center bg-blue-50 text-blue-900">
                        {formatCurrency(grandTotalPremium)}
                      </TableCell>
                      {canEdit && isAdmin() && <TableCell></TableCell>}
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      {/* Add Producer Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add Producer</DialogTitle>
            <DialogDescription>
              Enter the name of the sales producer to add to the pipeline tracking.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="producer-name">Producer Name</Label>
            <Input
              id="producer-name"
              value={newProducerName}
              onChange={(e) => setNewProducerName(e.target.value)}
              placeholder="e.g., John Smith"
              className="mt-2"
              onKeyDown={(e) => e.key === 'Enter' && handleAddProducer()}
              data-testid="producer-name-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddProducer} disabled={saving} data-testid="save-producer-btn">
              {saving ? 'Adding...' : 'Add Producer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Producer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{producerToDelete?.producer_name}"? 
              All pipeline data for this producer will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteProducer}
              disabled={saving}
            >
              {saving ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Excel Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Upload Pipeline Data</DialogTitle>
            <DialogDescription>
              Upload an Excel file from your broker management system to automatically populate the producer pipeline.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
              <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-sm text-slate-600 mb-4">
                Select an Excel file (.xlsx or .xls) containing producer pipeline data
              </p>
              <p className="text-xs text-slate-400 mb-4">
                Expected columns: Employee Name, Opportunity Stage, Qualified, Discover, Market, Propose
              </p>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleUploadFile}
                  className="hidden"
                  disabled={uploading}
                />
                <Button 
                  variant="outline" 
                  className="pointer-events-none"
                  disabled={uploading}
                >
                  {uploading ? (
                    <>Uploading...</>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Choose File
                    </>
                  )}
                </Button>
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ProducerPipeline;
